import React from 'react';
import Joi from 'joi';
import PropTypes from 'prop-types';

import Button from '../themed/button';
import Conditional from '../common/conditional';
import Form from '../common/form';
import Progress from '../themed/progress';

import { USER_TYPES } from '../../utils/constants';

import * as authService from '../../services/authService';
import * as roleService from '../../services/roleService';
import * as toastService from '../../services/toastService';

import * as models from '../../utils/models';
import * as utils from '../../utils/utilities';
import * as data from '../../utils/data';

class EditRole extends Form {
	state = {
		data: {
			name: '',
			description: '',
			userType: '',
			manageProjects: false,
			manageUsers: false,
			manageRoles: false,
			manageReports: false,
			reviewReports: false,
			approveReports: false,
			previewAllReports: false,
			viewReports: false,
			viewAssociatedReports: false,
		},
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
		name: Joi.string().min(3).required().label('Role Name'),
		description: Joi.string().min(8).required().label('Role Name'),
		userType: Joi.string().min(3).required().label('User Type'),
		manageProjects: Joi.boolean().required().label('Manage Project'),
		manageUsers: Joi.boolean().required().label('Manage Users'),
		manageRoles: Joi.boolean().required().label('Manage Roles'),
		reviewReports: Joi.boolean().required().label('Review Report'),
		approveReports: Joi.boolean().required().label('Approve Report'),
		manageReports: Joi.boolean().required().label('Manage Reports'),
		previewAllReports: Joi.boolean().required().label('Preview All Reports'),
		viewReports: Joi.boolean().required().label('View All Reports'),
		viewAssociatedReports: Joi.boolean()
			.required()
			.label('View Associated Reports'),
	};

	static propTypes = {
		role: PropTypes.object,
	};

	componentDidMount() {
		this.resetRole();
	}

	resetRole = (): void => {
		const role: models.Role | null = this.props.role;
		if (!role) return;

		this.setState({
			data: {
				name: role.name,
				description: role.description,
				manageProjects: role.manageProjects,
				manageUsers: role.manageUsers,
				manageRoles: role.manageRoles,
				manageReports: role.manageReports,
				reviewReports: role.reviewReports,
				approveReports: role.approveReports,
				viewReports: role.viewReports,
				previewAllReports: role.previewAllReports,
				userType: role.userType,
				viewAssociatedReports: role.viewAssociatedReports,
			},
		});
	};

	handleDeleteRole = async (): Promise<void> => {
		const role: models.Role | null = this.props.role;
		if (!role) return toastService.error('Role not found');

		await roleService.deleteRole(role.id);
		window.location.reload();
	};

	handleEditRole = async (): Promise<void> => {
		const role: models.Role = this.props.role;
		const { data } = this.state;

		await roleService.editRole(role.id, data, this.handleProgressChange);

		const user = authService.getCurrentUser() as models.User;
		if (role.id === user.role.id) await authService.refreshUserSession();

		window.location.reload();
	};

	deleteRole = (): void => {
		toastService.promise(this.handleDeleteRole(), {
			loading: 'Deleting Role',
			success: 'Role Deleted Successfully',
			error: utils.getErrorMessage,
		});
	};

	doSubmit = (): void => {
		toastService.promise(this.handleEditRole(), {
			loading: 'Updating Role',
			success: 'Role Updated',
			error: utils.getErrorMessage,
		});
	};

	render() {
		return (
			<React.Fragment>
				<Progress progress={this.state.progress} />

				<form autoComplete="off" onSubmit={this.handleSubmit} className="p-3">
					<h1 className="form-title">Modify Role</h1>

					<div className="divider my-3"></div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'name',
								label: 'Role Name',
								placeholder: 'Enter role name',
								disabled: true,
							})}
						</div>
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'description',
								label: 'Role Description',
								placeholder: 'Enter role description',
								disabled: true,
							})}
						</div>
						<div className="col">
							{this.renderSelect({
								name: 'userType',
								label: 'User Type',
								placeholder: 'Select User Type',
								options: data.getUserTypes(),
							})}
						</div>
					</div>

					<Conditional isVisible={this.state.data.userType === USER_TYPES.INTERNAL}>
						<React.Fragment>
							<div className="row mt-3">
								<div className="col">
									{this.renderSwitch({
										name: 'manageProjects',
										label: 'Manage Projects',
									})}
								</div>
								<div className="col">
									{this.renderSwitch({
										name: 'manageUsers',
										label: 'Manage Users',
									})}
								</div>
								<div className="col">
									{this.renderSwitch({
										name: 'manageRoles',
										label: 'Manage Roles',
									})}
								</div>
								<div className="col">
									{this.renderSwitch({
										name: 'manageReports',
										label: 'Manage Reports',
									})}
								</div>
							</div>

							<div className="divider my-4"></div>

							<div className="row">
							<div className="col">
									{this.renderSwitch({
										name: 'previewAllReports',
										label: 'Preview All Reports',
									})}
								</div>
								
								<div className="col">
									{this.renderSwitch({
										name: 'reviewReports',
										label: 'Review Report',
									})}
								</div>

								<div className="col">
									{this.renderSwitch({
										name: 'approveReports',
										label: 'Approve Report',
									})}
								</div>
							</div>
						</React.Fragment>
					</Conditional>

					<Conditional isVisible={this.state.data.userType === 'external user'}>
						<div className="row mt-4">
							<div className="col">
								{this.renderSwitch({
									name: 'viewReports',
									label: 'View All Reports',
								})}
							</div>

							<div className="col">
								{this.renderSwitch({
									name: 'viewAssociatedReports',
									label: 'View Associated Reports Only',
								})}
							</div>
						</div>
					</Conditional>

					<div className="divider my-4"></div>

					<div className="d-flex justify-content-end">
						<div className="mx-3">{this.renderButton('Modify')}</div>

						<Button
							type="button"
							label=""
							icon="trash"
							onClick={this.deleteRole}
						/>
					</div>
				</form>
			</React.Fragment>
		);
	}
}

export default EditRole;
